import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Button, useMantineTheme } from '@mantine/core';
import * as Cesium from 'cesium';
import { AppInfoContext } from '../../../../../components/contexts/AppInfoContext';
import { BehaviorParameter } from '../../../../../types/behaviors';
import { BehaviorEditiorContext } from '../contexts/BehaviorEditiorContext';
import { clearCircles } from '../functions/clearCircles';
import { clearLines } from '../functions/clearLines';
import { getArrowMaterial } from '../functions/getArrowMaterial';
import { getCenterFromCartesians } from '../functions/getCenterFromCartesians';

interface IPathLayer {
	viewer: Cesium.Viewer;
	defaultValue: any;
	isEditionModeOn: boolean;
	systemIds: { agent_id: number; system_id: number; is_commander: boolean };
	previewMode?: boolean;
	elementOnWhichInputDepends?: BehaviorParameter;
	isEchelonSettings?: boolean;
	queueInputIndex?: number; // If belongs to queue input
	onChange: (value: any) => void;
	setLayerControls?: (value: JSX.Element | null) => void;
}

const PathLayer: React.FC<IPathLayer> = ({
	viewer,
	defaultValue,
	previewMode,
	isEditionModeOn,
	systemIds,
	queueInputIndex,
	elementOnWhichInputDepends,
	isEchelonSettings,
	onChange,
	setLayerControls,
}) => {
	const theme = useMantineTheme();
	const { isSuperuser } = useContext(AppInfoContext);
	const { allEntities, updateAllEntities, resetAgentIdEntities } =
		useContext(BehaviorEditiorContext);

	const [currentArea, setCurrentArea] = useState<
		(Cesium.Entity & { properties: Record<string, any> }) | null
	>(null);
	const [selectedNodes, setSelectedNodes] = useState<number[]>(
		defaultValue.length === 0 ? [] : defaultValue
	); // Store selected node IDs
	const [pathLines, setPathLines] = useState<Cesium.Entity[]>([]); // Store path line entities
	const [currentlySelecting, setCurrentlySelecting] = useState(false);

	const color = useMemo(
		() => new Cesium.ColorMaterialProperty(Cesium.Color.WHITE.withAlpha(0.2)),
		[]
	);
	const chosenColor = useMemo(
		() =>
			new Cesium.ColorMaterialProperty(
				Cesium.Color.fromCssColorString(theme.colors.appMainColor[5]).withAlpha(0.8)
			),
		[theme]
	);

	const updateCurrentArea = useCallback(() => {
		const areas = viewer.dataSources?.getByName('areas')[0]?.entities
			?.values as (Cesium.Entity & {
			properties: Record<string, any>;
		})[];

		if (areas) {
			const currentArea =
				areas.find((x) => x.properties.type._value.toLowerCase().includes('staging')) ||
				null;
			setCurrentArea(currentArea);
		}
	}, [viewer]);

	const drawPath = useCallback(() => {
		const newLines: Cesium.Entity[] = [];
		const entityType = isEchelonSettings ? 'echelon' : 'symbol';

		if (selectedNodes.length >= 1) {
			const startEntity = viewer.entities.getById(entityType + systemIds.agent_id);
			let startingPosition = Cesium.Cartographic.fromCartesian(
				startEntity?.position?.getValue(new Cesium.JulianDate(1, 1)) as Cesium.Cartesian3
			);
			let previousNodeId = startEntity?.id;

			if (elementOnWhichInputDepends && elementOnWhichInputDepends.value) {
				const lastPosition =
					elementOnWhichInputDepends.property === 'PATH'
						? (elementOnWhichInputDepends.value as number[])[
								(elementOnWhichInputDepends.value as number[]).length - 1
							]
						: elementOnWhichInputDepends.value;

				startingPosition = getCenterFromCartesians(
					viewer?.dataSources
						?.getByName('geometry_voronois')[0]
						.entities.values.find((entity) => entity.id === `spm-cell-${lastPosition}`)
						?.polygon?.hierarchy?.getValue(new Cesium.JulianDate(1, 1)).positions
				);

				previousNodeId = lastPosition.toString();
			}

			let previousPosition = Cesium.Cartesian3.fromRadians(
				startingPosition.longitude,
				startingPosition.latitude,
				startingPosition.height
			);

			selectedNodes.forEach((nodeId, index) => {
				const exists = viewer.entities.getById(
					`line-${entityType}${systemIds.agent_id}-${index}-from${previousNodeId}-to${nodeId}${
						queueInputIndex ? `-${queueInputIndex}` : ''
					}`
				);
				if (exists) {
					viewer.entities.remove(exists);
				}

				const currentNode = viewer.dataSources
					.getByName('geometry_voronois')[0]
					.entities.getById(`spm-cell-${nodeId}`);

				if (currentNode?.polygon?.hierarchy) {
					const currentEntityCenter = getCenterFromCartesians(
						currentNode.polygon.hierarchy.getValue(new Cesium.JulianDate(1, 1))
							.positions
					);
					const currentPosition = Cesium.Cartesian3.fromRadians(
						currentEntityCenter.longitude,
						currentEntityCenter.latitude,
						currentEntityCenter.height
					);

					const line = viewer.entities.add({
						id: `line-${entityType}${systemIds.agent_id}-${index}-from${previousNodeId}-to${nodeId}${
							queueInputIndex ? `-${queueInputIndex}` : ''
						}`,

						polyline: {
							positions: [previousPosition, currentPosition],
							width: 10,
							material: getArrowMaterial(previewMode),
						},
					});

					newLines.push(line);
					previousNodeId = nodeId.toString();
					previousPosition = currentPosition; // Set the end position of the current line as the start position for the next line
				}
			});
		}

		updateAllEntities(
			entityType +
				systemIds.agent_id.toString() +
				(queueInputIndex ? `-${queueInputIndex}` : ''),
			'path',
			newLines
		);
		setPathLines(newLines);
	}, [
		viewer,
		selectedNodes,
		systemIds,
		queueInputIndex,
		elementOnWhichInputDepends,
		isEchelonSettings,
		previewMode,
	]);

	const setSelectedNodeId = useCallback((selectedEntity: Cesium.Entity) => {
		if (selectedEntity) {
			const entityId = parseInt(selectedEntity.id.split('spm-cell-')[1]);
			setSelectedNodes((prev) => [...prev, entityId]);
		}
	}, []);

	const toggleEditionMode = () => {
		const entityType = isEchelonSettings ? 'echelon' : 'symbol';

		if (!previewMode && !currentlySelecting) {
			setCurrentlySelecting(true);

			// Batch Cesium entity mutations so collectionChanged fires once instead of per removal.
			viewer.entities.suspendEvents();
			try {
				clearLines(viewer, entityType + systemIds.agent_id);
				clearCircles(viewer, systemIds.agent_id.toString());

				// Reset only polygons/lines/circles that were actually highlighted by other agents
				// (via the shared BehaviorEditior context) instead of scanning every voronoi cell.
				// Scanning the full voronoi mesh and reassigning a fresh ColorMaterialProperty per
				// cell was causing multi-second stalls before entering path edition mode.
				const ownKey = entityType + systemIds.agent_id.toString();
				Object.keys(allEntities).forEach((key) => {
					if (key === ownKey || key.split('-')[0] === ownKey) return;
					resetAgentIdEntities(key, false);
				});
			} finally {
				viewer.entities.resumeEvents();
			}
		}
		if (currentlySelecting) {
			setCurrentlySelecting(false);
		}
	};

	// useEffect on elementOnWhichInputDepends.value to remove first line from path (leftover if we remove some input from the queue)
	useEffect(() => {
		if (queueInputIndex) {
			const entityType = isEchelonSettings ? 'echelon' : 'symbol';
			resetAgentIdEntities(
				entityType +
					systemIds.agent_id.toString() +
					(queueInputIndex ? `-${queueInputIndex + 1}` : '')
			);
		}
	}, [queueInputIndex, viewer, systemIds]);

	useEffect(() => {
		updateCurrentArea();
	}, [updateCurrentArea]);

	useEffect(() => {
		!previewMode &&
			currentlySelecting &&
			viewer.selectedEntityChanged.addEventListener(setSelectedNodeId);

		return () => {
			!previewMode &&
				currentlySelecting &&
				viewer.selectedEntityChanged.removeEventListener(setSelectedNodeId);
		};
	}, [
		viewer,
		currentlySelecting,
		color,
		chosenColor,
		defaultValue,
		previewMode,
		setSelectedNodeId,
	]);

	useEffect(() => {
		if (currentArea) {
			const entityType = isEchelonSettings ? 'echelon' : 'symbol';
			resetAgentIdEntities(
				entityType +
					systemIds.agent_id.toString() +
					(queueInputIndex ? `-${queueInputIndex}` : '')
			);
			drawPath();
		}
	}, [currentArea, selectedNodes, drawPath, queueInputIndex, systemIds, isEchelonSettings]);

	useEffect(() => {
		setSelectedNodes(defaultValue);
	}, [defaultValue]);

	// Setup layer controls
	useEffect(() => {
		const resetPath = () => {
			setSelectedNodes([]);
			pathLines.forEach((line) => viewer.entities.remove(line));
			setPathLines([]);
		};

		if (currentlySelecting && setLayerControls) {
			setLayerControls(
				<div
					style={{
						position: 'absolute',
						bottom: '0',
						right: '0',
						margin: '1rem',
						display: 'flex',
						gap: '1rem',
					}}
				>
					<Button
						onClick={() => {
							onChange(selectedNodes);
							setCurrentlySelecting(false);
						}}
						disabled={!isSuperuser}
					>
						Confirm
					</Button>
					<Button onClick={resetPath}>Reset</Button>
				</div>
			);
		} else if (setLayerControls) {
			setLayerControls(null);
		}
	}, [selectedNodes, isSuperuser, currentlySelecting, pathLines]);

	return !previewMode ? (
		<Button disabled={isEditionModeOn && !currentlySelecting} onClick={toggleEditionMode}>
			{currentlySelecting ? 'Stop setting' : 'Start setting'}
		</Button>
	) : null;
};

export default PathLayer;
